// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */, {
  type Node,
} from 'react';

import SkipLink from '@zapier/design-system-patterns/src/components/UniversalNav/SkipLink';
import UniversalNavLink from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalNavLink';
import { UNIVERSAL_TOPBAR_CONDENSED_HEIGHT } from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalTopbar';
import { ZapierLogo } from '@zapier/design-system-patterns';
import { Zindexes } from '@zapier/theme';
import { normalizePathname } from '@zapier/url-utils';
import { Animation, Colors, Link } from '@zapier/design-system';
import {
  UniversalSidebarLinks,
  skipLinkHref,
} from '@zapier/design-system-patterns/src/components/UniversalNav/constants';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import type { UniversalNavLinkType } from '@zapier/design-system-patterns/src/components/UniversalNav/types';

/** @jsx isolateComponent */

export const UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH = 70;

export type Props = {|
  /**
   * Label describing the collapse button.
   */
  collapseButtonLabel?: string,
  /**
   * Indicates whether the navigation is collapsed down to icons.
   */
  isCollapsed: boolean,
  /**
   * Whether the sidebar is as wide as it can be.
   * When `true`, will render as wide as it can.
   * When `false`, will constrain to a `max-width`.
   */
  isFullWidth: boolean,
  /**
   * Whether the sidebar is pinned to the side of the screen.
   */
  isPinned: boolean,
  /**
   * Array of links to render in the sidebar.
   */
  links: Array<UniversalNavLinkType>,
  /**
   * Label describing where the Zapier logo links to.
   */
  logoAriaLabel: string,
  /**
   * Where the Zapier logo links to.
   */
  logoHref: string,
  /**
   * Optional handler for when anywhere on the nav is clicked.
   */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  /**
   * Optional handler for when the `nav` is hovered.
   */
  onMouseEnter?: (SyntheticMouseEvent<HTMLElement>) => void,
  /**
   Optional handler for when the `nav` is unhovered.
   */
  onMouseLeave?: (SyntheticMouseEvent<HTMLElement>) => void,
  /**
   * Renders content above the links. Typically that content is
   * a CTA button, such as to create a Zap.
   */
  renderSubheader?: ({}) => Node,
  /**
   * Renders content below the links. Typically that content is
   * usage data or CTA buttons.
   */
  renderFooter: () => Node,
  /**
   * The `href` of the currently selected link within `links`.
   */
  selectedHref: string,
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref: string,
|};

const Styles = labelStyles(__filename, {
  root: props => [
    {
      position: 'relative',
      display: 'grid',
      gridTemplateRows: 'min-content 1fr',
      // Necessary for Safari to render properly.
      gridTemplateColumns: '100%',
      gridTemplateAreas: `
        "header"
        "content"
      `,
      gap: 20,
      backgroundColor: Colors.white,
      padding: 10,
      maxHeight: '100%',
      // Necessary to set explicitly so `transition` works.
      width: '100%',
      transition: Animation.transitionValue,
      // Noticed some jankiness with `transition` and `animation`.
      // This appears to improve it at least a bit.
      willChange: 'transform, width',
      overflow: 'hidden',
    },

    !props.isFullWidth &&
      withModifier('constrainedWidth', {
        width: 300,
        maxWidth: '100vw',
      }),

    !props.isFullWidth &&
      props.isPinned &&
      withModifier('floating', {
        // Add a `border-right` w/o the border actually taking
        // up any box model space. `border-right` reduces the inner
        // width of the content and causes horizontal scrolling
        // which makes this necessary.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 1,
          backgroundColor: Colors.grey200,
        },
      }),

    !props.isFullWidth &&
      props.isPinned &&
      !props.isCollapsed &&
      withModifier('shadowed', {
        boxShadow: `
        3px 0 10px rgba(0, 0, 0, 0.05),
        2px 0 5px rgba(0, 0, 0, 0.05),
        1px 0 3px rgba(0, 0, 0, 0.05)
      `,
      }),

    props.isPinned &&
      withModifier('pinned', {
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: Zindexes.universalNav,
      }),

    props.isFullWidth &&
      props.isPinned &&
      withModifier('fullScreen', {
        top: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
      }),

    props.isCollapsed &&
      withModifier('collapsed', {
        width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        overflow: 'visible',
      }),
  ],

  header: {
    gridArea: 'header',
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: 15,
  },

  logo: (props: Props) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      overflow: 'hidden',
    },
    // Hide the logo when collapsed so it can transition
    // out rather than disappear abruptly.
    props.isCollapsed &&
      withModifier('collapsed', {
        opacity: 0,
      }),
  ],

  // Skip link for accessibility to allow screenreader
  // users to easily skip over the main navigation.
  skipLink: {
    position: 'absolute',
    top: '100%',
    left: 0,
    textAlign: 'center',
    width: '100%',
    padding: 2,
    whiteSpace: 'nowrap',
  },

  content: (props: Props) => [
    {
      gridArea: 'content',
      // On devices that don't support hovering, ensure that
      // the sidebar can be scrolled since it's typically only
      // scrollable when expanded.
      '@media (hover: none)': {
        overflow: 'auto',
      },
    },
    !props.isCollapsed &&
      withModifier('expanded', {
        overflow: 'auto',
      }),
  ],

  contentInner: {
    display: 'grid',
    gridTemplateRows: 'min-content',
    // Setting this explicitly is important for making the width
    // transition smooth and helps ensure nothing leaks out of the
    // sidebar when transitioning width.
    gridTemplateColumns: '100%',
    gap: 20,
  },

  links: () => [
    {
      display: 'grid',
      alignContent: 'start',
      gap: 20,
    },
  ],

  footer: {
    borderTop: `1px solid ${Colors.grey200}`,
    paddingTop: 20,
  },
});

const Header = (props: Props) => {
  const subheaderContent =
    !!props.renderSubheader && props.renderSubheader(props);
  const hasLogo = !props.isFullWidth;
  return (
    <div css={Styles.header}>
      {hasLogo && (
        <div css={Styles.logo(props)}>
          <Link
            ariaLabel={props.logoAriaLabel}
            color={null}
            href={props.logoHref}
          >
            <ZapierLogo />
          </Link>
        </div>
      )}
      <div css={Styles.skipLink}>
        <SkipLink
          href={props.skipLinkHref}
          text={props.isCollapsed ? 'Skip' : 'Skip to content'}
        />
      </div>
      {!!subheaderContent && <div>{subheaderContent}</div>}
    </div>
  );
};

// Indicates whether the given `href` should be considered selected
// based on the value of `selectedHref`. The rules of this are somewhat
// specific to `UniversalSidebar`, so be careful if you try to make
// this more generic!
export const isLinkSelected = (href: string, selectedHref: string) => {
  return normalizePathname(href).endsWith(normalizePathname(selectedHref));
};

const Links = (props: Props) => {
  const links = props.links.map(link => (
    <li key={link.href}>
      <UniversalNavLink
        href={link.href}
        icon={link.icon}
        isCollapsed={props.isCollapsed}
        isSelected={isLinkSelected(link.href, props.selectedHref)}
        text={link.text}
      />
    </li>
  ));
  return <ul css={Styles.links()}>{links}</ul>;
};

/**
 * The universal sidebar that renders when users are logged in and
 * on an app page. Renders the universal navigation, a content footer,
 * and some other small bites.
 */
const UniversalSidebar = (props: Props) => {
  const mouseProps = {
    onClick: props.onClick,
    onMouseEnter: props.onMouseEnter,
    onMouseLeave: props.onMouseLeave,
  };
  const footerContent = props.renderFooter();
  const footer = footerContent && (
    <div css={Styles.footer}>{footerContent}</div>
  );
  return (
    <nav
      css={Styles.root(props)}
      data-testid="universalSidebar"
      {...mouseProps}
    >
      <Header {...props} />
      <div css={Styles.content(props)}>
        <div css={Styles.contentInner}>
          <Links {...props} />
          {footer}
        </div>
      </div>
    </nav>
  );
};

UniversalSidebar.defaultProps = {
  isCollapsed: false,
  isFullWidth: true,
  isPinned: true,
  links: UniversalSidebarLinks,
  logoHref: '/',
  logoAriaLabel: 'Go to homepage',
  renderFooter: () => null,
  selectedHref: '',
  skipLinkHref,
};

export default UniversalSidebar;
